import React from 'react'
import {useSelector} from 'react-redux'
import {PriceWatchCard} from 'components/Accommodation/PriceWatchCard/PriceWatchCard'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getSplitBookingDetails} from 'modules/sapiSearch/selectors'
import {getTopOffersWithSplitBookings} from 'modules/sapiSearch/utils'
import {getIsNoDatesSearch} from 'modules/search/selectors'

import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {HotelOfferCard, HotelOfferCardSkeleton} from '../../HotelOfferCard'
import {useAccommodationTopOffers} from '../../useAccommodationTopOffers'
import {useShowAllOffersFromUrl} from '../../useShowAllOffersFromUrl'
import {AllOffersDesktopOverlay} from './AllOffersDesktopOverlay'
import {MoreDealsDesktopButton} from './MoreDealsDesktopButton'
import {
  LayoutVariant,
  TOP_OFFERS_COUNT,
  TopOffersDesktopLayout
} from './TopOffersDesktopLayout'
import {TopOffersDesktopSoldOut} from './TopOffersDesktopSoldOut'

const SOURCE_COMPONENT = 'DesktopHotelViewTopOffers'

export const TopOffersDesktopRedesigned = () => {
  const hotelId = useSelector(getAccommodationPageHotelId)
  const {offerEntity, isComplete, hasAnyOffers} = useAccommodationTopOffers({
    hotelId
  })
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  // Reuse the shared split-booking merge so the injected bundle and resulting
  // offer ordering match what the A-side OfferBox shows.
  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId ?? '')
  )
  const offers = offerEntity
    ? getTopOffersWithSplitBookings(offerEntity, splitBookingDetails)
    : []

  const {hasSplitBookingOffer} = splitBookingDetails

  const {retention} = useBrandCapabilities()
  const {hasPriceWatch} = retention

  const isLoading = !hasAnyOffers && !isNoDatesSearch
  const isSoldout = isComplete && !(hasSplitBookingOffer || hasAnyOffers)

  // A lone offer with no price-watch cell renders full-width instead of a
  // single narrow card.
  const variant: LayoutVariant =
    offers.length === 1 && !hasPriceWatch ? 'horizontal' : 'vertical'

  const topOffers = offers.slice(0, TOP_OFFERS_COUNT)

  const hasMoreOffers = (offerEntity?.offers?.length ?? 0) > TOP_OFFERS_COUNT
  useShowAllOffersFromUrl({
    hotelId,
    hasMoreOffers: isComplete && hasMoreOffers
  })

  const offerCards = isLoading
    ? Array.from({length: TOP_OFFERS_COUNT}).map((_, index) => (
        <HotelOfferCardSkeleton key={index} />
      ))
    : topOffers.map((offer, index) => (
        <HotelOfferCard
          key={offer.id}
          offer={offer}
          offerPosition={index}
          sourceComponent={SOURCE_COMPONENT}
          variant={variant}
        />
      ))

  return (
    <>
      <AllOffersDesktopOverlay />
      <Box display="flex" flexDirection="column" gap="s500">
        <Text variant="titleL">
          <FormattedMessageWrapper
            defaultMessage="Available deals"
            id="availableDealsTopOffers"
          />
        </Text>
        {isSoldout ? (
          <TopOffersDesktopSoldOut />
        ) : (
          <TopOffersDesktopLayout
            offerCards={offerCards}
            priceWatchSection={
              hasPriceWatch ? (
                <PriceWatchCard offersLength={offers.length} />
              ) : null
            }
            moreDealsButtonSlot={
              hasMoreOffers ? (
                <MoreDealsDesktopButton hotelId={hotelId} />
              ) : null
            }
          />
        )}
      </Box>
    </>
  )
}
